import { useCallback, useEffect, useState } from "react";

const CACHE_PREFIX = "elysium.cache:";
const CACHEABLE = [/^\/tech\//, /^\/lookups$/, /^\/auth\/me$/, /^\/hr\/(absences|shifts)$/];
const DB_NAME = "elysium-offline";
const STORE = "outbox";
const EVT = "outbox:changed";

export const cacheKey = (cfg) => `${CACHE_PREFIX}${cfg.url}?${JSON.stringify(cfg.params || {})}`;
export const isCacheable = (cfg) => (cfg.method || "get") === "get" && CACHEABLE.some((rx) => rx.test(cfg.url || ""));

export function readCache(cfg) {
  try {
    const raw = localStorage.getItem(cacheKey(cfg));
    return raw ? JSON.parse(raw) : undefined;
  } catch (e) {
    return undefined;
  }
}

export function writeCache(cfg, data) {
  try {
    localStorage.setItem(cacheKey(cfg), JSON.stringify(data));
  } catch (e) {
    // quota piena: la cache è un aiuto, non un requisito
  }
}

export function clearCache() {
  Object.keys(localStorage).filter((k) => k.startsWith(CACHE_PREFIX)).forEach((k) => localStorage.removeItem(k));
}

export const isNetworkError = (e) => !!e && !e.response;

function openDb() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => req.result.createObjectStore(STORE, { keyPath: "id", autoIncrement: true });
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function tx(mode, fn) {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const t = db.transaction(STORE, mode);
    const result = fn(t.objectStore(STORE));
    t.oncomplete = () => resolve(result?.result);
    t.onerror = () => reject(t.error);
  });
}

const notify = () => window.dispatchEvent(new Event(EVT));

export const listOutbox = () => tx("readonly", (s) => s.getAll()).then((x) => x || []);

export async function enqueue(item) {
  await tx("readwrite", (s) => s.add({ ...item, status: "pending", error: null, queued_at: new Date().toISOString() }));
  notify();
}

export async function removeItem(id) {
  await tx("readwrite", (s) => s.delete(id));
  notify();
}

async function putItem(item) {
  await tx("readwrite", (s) => s.put(item));
}

let syncing = null;

export function syncOutbox(api) {
  syncing = syncing || (async () => {
    const items = (await listOutbox()).sort((a, b) => a.id - b.id);
    const blocked = new Set();
    let sent = 0;
    for (const item of items) {
      if (blocked.has(item.order_id)) continue;
      if (item.status === "error") { blocked.add(item.order_id); continue; }
      try {
        await sendItem(api, item);
        await tx("readwrite", (s) => s.delete(item.id));
        sent += 1;
      } catch (e) {
        if (isNetworkError(e)) break;
        await putItem({ ...item, status: "error", error: e.response?.data?.detail?.msg || e.response?.data?.detail || "Errore di invio" });
        blocked.add(item.order_id);
      }
    }
    notify();
    return sent;
  })().finally(() => { syncing = null; });
  return syncing;
}

export async function retryItem(id) {
  const item = (await listOutbox()).find((x) => x.id === id);
  if (item) await putItem({ ...item, status: "pending", error: null });
  notify();
}

function sendItem(api, item) {
  const base = `/tech/orders/${item.order_id}`;
  if (item.type === "arrive") return api.post(`${base}/arrive`, { client_at: item.at });
  if (item.type === "report") return api.post(`${base}/report`, { ...item.payload, client_at: item.at });
  const fd = new FormData();
  fd.append("work_order_id", item.order_id);
  fd.append("kind", "foto");
  fd.append("file", item.payload.file, item.payload.name);
  return api.post("/attachments", fd);
}

export function applyOutbox(order, items) {
  if (!order) return order;
  const mine = items.filter((x) => x.order_id === order.id);
  if (!mine.length) return order;
  const o = { ...order, pending: mine };
  const arrive = mine.find((x) => x.type === "arrive");
  if (arrive && o.status === "PLANNED") {
    o.status = "IN_PROGRESS";
    o.visit = { id: "offline", arrived_at: arrive.at, attempt_no: (o.attempts || 0) + 1 };
  }
  const report = mine.find((x) => x.type === "report");
  if (report) {
    o.status = "COMPLETED";
    o.outcome = report.payload.outcome;
  }
  return o;
}

export function useOnline() {
  const [online, setOnline] = useState(navigator.onLine);
  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => { window.removeEventListener("online", on); window.removeEventListener("offline", off); };
  }, []);
  return online;
}

export function useOutbox() {
  const [items, setItems] = useState([]);
  const refresh = useCallback(() => listOutbox().then(setItems).catch(() => setItems([])), []);
  useEffect(() => {
    refresh();
    window.addEventListener(EVT, refresh);
    return () => window.removeEventListener(EVT, refresh);
  }, [refresh]);
  return items;
}

export async function sendOrQueue(api, item, items) {
  const waiting = items.some((x) => x.order_id === item.order_id);
  if (!waiting && navigator.onLine) {
    try {
      await sendItem(api, item);
      return { queued: false };
    } catch (e) {
      if (!isNetworkError(e)) throw e;
    }
  }
  await enqueue(item);
  return { queued: true };
}
